import React from 'react';
import type { WorkProgressBagCustomerMawbSummaryRow } from '../graphql/queries/getWorkProgressBagCustomerBreakdown';

/** 大包/包裹差异率公用色阶：> 10% 红，> 0 橙，<= 0 绿。 */
export function bigBagDiffRateTone(rate: number): { barClass: string; textClass: string; tagColor: 'red' | 'orange' | 'green' } {
  const r = Number(rate ?? 0);
  if (r > 0.1) return { barClass: 'bg-red-500', textClass: 'text-red-600', tagColor: 'red' };
  if (r > 0) return { barClass: 'bg-orange-500', textClass: 'text-orange-500', tagColor: 'orange' };
  return { barClass: 'bg-emerald-500', textClass: 'text-emerald-600', tagColor: 'green' };
}

/** 状态分级（与 [bigBagDiffRateTone] 同口径）。 */
export type BigBagStatus = 'high' | 'mid' | 'normal';
export function bigBagStatusKey(rate: number): BigBagStatus {
  const r = Number(rate ?? 0);
  if (r > 0.1) return 'high';
  if (r > 0) return 'mid';
  return 'normal';
}

/** 一级 / 二级 / 三级「差异率」列共用：百分比在上、进度条在下。 */
export function BigBagParcelDiffRateCell({ rate }: { rate: number }) {
  const r = Number(rate ?? 0);
  const pct = Math.max(0, Math.min(1, r));
  const widthStyle = `${Math.round(pct * 100)}%`;
  const { barClass, textClass } = bigBagDiffRateTone(r);
  return (
    <div className="flex min-w-0 w-full flex-col gap-1.5 pr-3">
      <div className={`shrink-0 text-sm font-semibold tabular-nums leading-none ${textClass}`}>
        {(r * 100).toFixed(2)}%
      </div>
      <div className="min-w-0 h-2 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: widthStyle }} />
      </div>
    </div>
  );
}

/** 可下钻文本统一样式：紫色下划线 + hover 加深。 */
export const CLICKABLE_UNDERLINE =
  'cursor-pointer underline underline-offset-2 decoration-violet-400 hover:decoration-violet-500';

/** 「有提单号」二级：同一 MAWB 多日到港行合并为单行；日期在三级每个大包号下展示。 */
export function mergeWithMawbRowsByMawb(rows: WorkProgressBagCustomerMawbSummaryRow[]): WorkProgressBagCustomerMawbSummaryRow[] {
  type Acc = {
    expectedBagCountSum: number;
    inboundBagCountSum: number;
    expectedParcelCountSum: number;
    inboundParcelCountSum: number;
  };
  const acc = new Map<string, Acc>();
  for (const r of rows) {
    const eb = Number(r.expectedBagCountSum ?? 0);
    const ibg = Number(r.inboundBagCountSum ?? 0);
    const ep = Number(r.expectedParcelCountSum ?? 0);
    const ip = Number(r.inboundParcelCountSum ?? 0);
    const cur = acc.get(r.mawb);
    if (!cur) {
      acc.set(r.mawb, {
        expectedBagCountSum: eb,
        inboundBagCountSum: ibg,
        expectedParcelCountSum: ep,
        inboundParcelCountSum: ip,
      });
    } else {
      cur.expectedBagCountSum += eb;
      cur.inboundBagCountSum += ibg;
      cur.expectedParcelCountSum += ep;
      cur.inboundParcelCountSum += ip;
    }
  }
  return Array.from(acc.entries()).map(([mawb, s]) => {
    const parcelDiffSum = s.expectedParcelCountSum - s.inboundParcelCountSum;
    const parcelDiffRate = s.expectedParcelCountSum > 0 ? parcelDiffSum / s.expectedParcelCountSum : 0;
    return {
      mawb,
      arrivalDate: '',
      bagCount: s.expectedBagCountSum,
      expectedBagCountSum: s.expectedBagCountSum,
      inboundBagCountSum: s.inboundBagCountSum,
      expectedParcelCountSum: s.expectedParcelCountSum,
      inboundParcelCountSum: s.inboundParcelCountSum,
      parcelDiffSum,
      parcelDiffRate,
    };
  });
}

/** client_carton 明细查询的扫描时间需 ±2 天扩展，兜底 UTC 跨日漂移。 */
export const CLIENT_CARTON_DATE_WINDOW_PAD_DAYS = 2;
